/**
 * Custom React Hook for Notifications API
 */

import { useState, useEffect, useCallback } from 'react';
import notificationService from '../services/notificationService';
import { NotificationDTO } from '../types/notification.types';

interface UseNotificationsProps {
  userId?: string;
  autoFetch?: boolean;
  unreadOnly?: boolean;
  pollingInterval?: number; // in milliseconds, default 30000 (30 seconds)
}

export const useNotifications = ({
  userId,
  autoFetch = true,
  unreadOnly = false,
  pollingInterval = 30000,
}: UseNotificationsProps = {}) => {
  const [notifications, setNotifications] = useState<NotificationDTO[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user notifications
  const fetchNotifications = useCallback(async (usrId: string) => {
    setLoading(true);
    setError(null);
    try {
      const notificationsList = unreadOnly
        ? await notificationService.getUnreadNotifications(usrId)
        : await notificationService.getUserNotifications(usrId);
      setNotifications(notificationsList);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async (usrId: string) => {
    try {
      const count = await notificationService.getUnreadNotificationCount(usrId);
      setUnreadCount(count);
    } catch (err: any) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    setError(null);
    try {
      await notificationService.markAsRead(notificationId);
      // Update local state
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: true, readAt: new Date().toISOString() }
            : notification
        )
      );
      // Update unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark notification as read');
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async (usrId: string) => {
    setError(null);
    try {
      await notificationService.markAllAsRead(usrId);
      // Update local state
      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
          readAt: new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark all notifications as read');
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, []);

  // Auto-fetch on mount and set up polling
  useEffect(() => {
    if (!userId || !autoFetch) return;

    // Initial fetch
    fetchNotifications(userId);
    fetchUnreadCount(userId);

    // Set up polling for real-time updates
    const intervalId = setInterval(() => {
      fetchNotifications(userId);
      fetchUnreadCount(userId);
    }, pollingInterval);

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [userId, autoFetch, pollingInterval, fetchNotifications, fetchUnreadCount]);

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,

    // Actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    setError,
  };
};
